import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Platform,
} from 'react-native';
import type { PatinfoNewRequestDto } from '../../../types/api/patinfo';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';

interface ManageClientsAddBarProps {
  onAdd: (payload: PatinfoNewRequestDto) => Promise<void>;
  onCancel: () => void;
  showPatId?: boolean;
  showDob?: boolean;
}

type MobileDateTimePickerType = React.ComponentType<{
  value: Date;
  mode: 'date';
  display: 'default';
  onChange: (event: any, selectedDate?: Date) => void;
}>;

interface AddFormState {
  fname: string;
  lname: string;
  patientid: string;
  dob: string;
}

const EMPTY_FORM: AddFormState = { fname: '', lname: '', patientid: '', dob: '' };

function validateAddForm(form: AddFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!form.fname.trim()) errors.fname = 'First name is required';
  if (!form.lname.trim()) errors.lname = 'Last name is required';
  if (form.dob.trim() && !/^\d{4}-\d{2}-\d{2}$/.test(form.dob.trim())) {
    errors.dob = 'Enter date as YYYY-MM-DD';
  }
  return errors;
}

export const ManageClientsAddBar: React.FC<ManageClientsAddBarProps> = ({
  onAdd,
  onCancel,
  showPatId = true,
  showDob = true,
}) => {
  const isWeb = Platform.OS === 'web';
  const [form, setForm] = useState<AddFormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showMobileDobPicker, setShowMobileDobPicker] = useState(false);
  const [MobileDateTimePicker, setMobileDateTimePicker] = useState<MobileDateTimePickerType | null>(null);

  // Determine if DOB should be in first column (under fname) or second column (under lname)
  const dobInFirstColumn = !showPatId && showDob;

  const setField = (field: keyof AddFormState, value: string) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) {
      setErrors(e => {
        const next = { ...e };
        delete next[field];
        return next;
      });
    }
  };

  const handleAdd = async () => {
    const validationErrors = validateAddForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setSaveError(null);
    setIsSaving(true);
    try {
      await onAdd({
        patid: form.patientid.trim(),
        fname: form.fname.trim(),
        lname: form.lname.trim(),
        dob: form.dob.trim(),
      });
      setForm(EMPTY_FORM);
      onCancel();
    } catch (err: any) {
      setSaveError(err?.message ?? 'Failed to save client. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMobileDobChange = (_event: any, selectedDate?: Date) => {
    setShowMobileDobPicker(false);
    if (selectedDate) {
      setField('dob', selectedDate.toISOString().slice(0, 10));
    }
  };

  const webDateInputStyle = { ...webControlStyle, width: 120, colorScheme: 'light' } as any;

  const webDateInputStyleEmpty = {
    ...webDateInputStyle,
    color: EDITOR_COLORS.placeholder,
  } as any;

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <View style={styles.container}>
      <Text style={styles.header}>Add New Client</Text>
      <View style={styles.tableCluster}>
        <View style={styles.columnLabel}>
          <Text style={[styles.label, errors.fname ? styles.labelError : null]}>First</Text>
          {showPatId && <Text style={[styles.label, errors.patientid ? styles.labelError : null]}>ID</Text>}
          {dobInFirstColumn && <Text style={[styles.label, errors.dob ? styles.labelError : null]}>DOB</Text>}
        </View>

        <View style={styles.columnInput}>
          <TextInput
            style={[styles.input, errors.fname ? styles.inputError : null]}
            value={form.fname}
            onChangeText={v => setField('fname', v)}
            autoCapitalize="words"
            returnKeyType="next"
          />
          {showPatId && (
            <TextInput
              style={[styles.input, errors.patientid ? styles.inputError : null]}
              value={form.patientid}
              onChangeText={v => setField('patientid', v)}
              autoCapitalize="none"
              returnKeyType="next"
            />
          )}
          {dobInFirstColumn && (
            isWeb ? (
              React.createElement('input' as any, {
                type: 'date',
                value: form.dob,
                onChange: (event: any) => setField('dob', String(event?.target?.value ?? '')),
                style: form.dob ? webDateInputStyle : webDateInputStyleEmpty,
                'aria-label': 'Date of birth',
              })
            ) : (
              MobileDateTimePicker ? (
                <>
                  <TouchableOpacity
                    style={[styles.mobileDateInputBtn, errors.dob ? styles.inputError : null]}
                    onPress={() => setShowMobileDobPicker(true)}
                    accessibilityRole="button"
                    accessibilityLabel="Select date of birth"
                  >
                    <Text style={[styles.mobileDateInputText, !form.dob && styles.mobileDateInputPlaceholder]}>
                      {form.dob || 'YYYY-MM-DD'}
                    </Text>
                  </TouchableOpacity>
                  {showMobileDobPicker ? (
                    <MobileDateTimePicker
                      value={form.dob ? new Date(`${form.dob}T00:00:00`) : new Date()}
                      mode="date"
                      display="default"
                      onChange={handleMobileDobChange}
                    />
                  ) : null}
                </>
              ) : (
                <TextInput
                  style={[styles.input, errors.dob ? styles.inputError : null]}
                  value={form.dob}
                  onChangeText={v => setField('dob', v)}
                  returnKeyType="done"
                  maxLength={10}
                  onSubmitEditing={handleAdd}
                  placeholder="YYYY-MM-DD"
                />
              )
            )
          )}
        </View>

        <View style={styles.columnSpacer} />

        <View style={styles.columnLabel}>
          <Text style={[styles.label, errors.lname ? styles.labelError : null]}>Last</Text>
          {!dobInFirstColumn && showDob && <Text style={[styles.label, errors.dob ? styles.labelError : null]}>DOB</Text>}
        </View>

        <View style={styles.columnInput}>
          <TextInput
            style={[styles.input, errors.lname ? styles.inputError : null]}
            value={form.lname}
            onChangeText={v => setField('lname', v)}
            autoCapitalize="words"
            returnKeyType="next"
          />
          {!dobInFirstColumn && showDob && (
            isWeb ? (
              React.createElement('input' as any, {
                type: 'date',
                value: form.dob,
                onChange: (event: any) => setField('dob', String(event?.target?.value ?? '')),
                style: form.dob ? webDateInputStyle : webDateInputStyleEmpty,
                'aria-label': 'Date of birth',
              })
            ) : (
              MobileDateTimePicker ? (
                <>
                  <TouchableOpacity
                    style={[styles.mobileDateInputBtn, errors.dob ? styles.inputError : null]}
                    onPress={() => setShowMobileDobPicker(true)}
                    accessibilityRole="button"
                    accessibilityLabel="Select date of birth"
                  >
                    <Text style={[styles.mobileDateInputText, !form.dob && styles.mobileDateInputPlaceholder]}>
                      {form.dob || 'YYYY-MM-DD'}
                    </Text>
                  </TouchableOpacity>
                  {showMobileDobPicker ? (
                    <MobileDateTimePicker
                      value={form.dob ? new Date(`${form.dob}T00:00:00`) : new Date()}
                      mode="date"
                      display="default"
                      onChange={handleMobileDobChange}
                    />
                  ) : null}
                </>
              ) : (
                <TextInput
                  style={[styles.input, errors.dob ? styles.inputError : null]}
                  value={form.dob}
                  onChangeText={v => setField('dob', v)}
                  returnKeyType="done"
                  maxLength={10}
                  onSubmitEditing={handleAdd}
                  placeholder="YYYY-MM-DD"
                />
              )
            )
          )}
        </View>

        <View style={styles.columnAction}>
          <TouchableOpacity
            onPress={handleAdd}
            style={[styles.addBtn, isSaving && styles.addBtnDisabled]}
            disabled={isSaving}
            accessibilityLabel="Add client"
          >
            {isSaving ? (
              <ActivityIndicator size="small" color={EDITOR_COLORS.submitText} />
            ) : (
              <Text style={styles.addBtnText}>Add</Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            onPress={onCancel}
            style={styles.cancelLink}
            accessibilityLabel="Cancel add client"
          >
            <Text style={styles.cancelLinkText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>

      {saveError ? (
        <View style={styles.errorRow}>
          <Text style={styles.errorText}>{saveError}</Text>
        </View>
      ) : null}
      {hasErrors ? (
        <View style={styles.errorRow}>
          <Text style={styles.errorText}>{Object.values(errors).join(' · ')}</Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: EDITOR_COLORS.background,
    borderRadius: 14,
    padding: 16,
    marginBottom: 20,
    alignItems: 'center',
  },
  header: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    textAlign: 'center',
    marginBottom: 12,
  },
  tableCluster: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 3,
  },
  columnLabel: {
    width: 31,
    justifyContent: 'flex-start',
    gap: 4,
  },
  columnInput: {
    width: 120,
    justifyContent: 'flex-start',
    gap: 4,
  },
  columnSpacer: {
    width: 8,
  },
  columnAction: {
    width: 60,
    justifyContent: 'flex-start',
    gap: 4,
    marginLeft: 3,
    alignItems: 'center',
  },
  label: {
    ...editorText,
    height: 26,
    lineHeight: 26,
    color: EDITOR_COLORS.textMuted,
  },
  labelError: {
    color: EDITOR_COLORS.errorText,
  },
  input: {
    ...editorText,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
    width: '100%',
  },
  inputError: {
    borderColor: EDITOR_COLORS.errorText,
  },
  addBtn: {
    width: 50,
    height: 26,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addBtnDisabled: {
    opacity: 0.6,
  },
  addBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  cancelLink: {
    height: 26,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelLinkText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  errorRow: {
    marginTop: 8,
    width: '100%',
    maxWidth: 500,
  },
  errorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
  },
  mobileDateInputBtn: {
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    justifyContent: 'center',
    backgroundColor: EDITOR_COLORS.background,
    width: '100%',
  },
  mobileDateInputText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  mobileDateInputPlaceholder: {
    color: EDITOR_COLORS.placeholder,
  },
});
